// ACTA_AFFAIRES_DASHBOARD_COMMERCIAL_FILTER_V52
// ACTA_AFFAIR_FINALIZATION_OPTIONAL_PROOF_V51
// ACTA_AFFAIRES_SECURE_SHARE_FLOW_V43
// ACTA_AFFAIRES_READY_PREVIEW_FINALIZATION_FIX_V42
// ACTA_AFFAIR_STEP_MODAL_SEND_FLOW_V49
// ACTA_AFFAIR_MODAL_MOBILE_V37
// ACTA_AFFAIR_MODAL_CENTERED_V36
// ACTA_AFFAIR_MODAL_DESIGN_V35
// ACTA_PIPELINE_ONE_VIEWPORT_V34
// ACTA_PIPELINE_QUALIFICATION_CONTACTS_V32
// ACTA_AFFAIRES_VALIDATED_FILTERS_V30
// ACTA_AFFAIRES_SCROLL_FILTER_REPAIR_V27
// ACTA_AFFAIRES_PREMIUM_SAAS_V23
// ACTA_AFFAIRES_FILTERS_UX_LOT1_3_V22
// ACTA_AFFAIRES_FILTERS_REALTIME_LOT1_2_V21
// ACTA_AFFAIRES_KANBAN_LOT1_V19
// ACTA_AFFAIRES_WORKFLOW_V18
// ACTA_AFFAIRES_UNIFIED_V17
// ACTA_PIPELINE_FINAL_MOCKUP_V15
// ACTA_KANBAN_POINTER_DRAG_SIMPLE_FILTER_V14
// ACTA_KANBAN_CARD_DRAG_CTA_V13
// ACTA_PIPELINE_DESIGN_PAGINATION_V3
// ACTA_OPPORTUNITY_PIPELINE_CLEAN_V1
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import {
  Briefcase,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CheckCircle,
  Eye,
  Funnel,
  MagnifyingGlass,
  PaperPlaneTilt,
  Plus,
  TrendUp,
  Trash,
  UserCircle,
  X,
  XCircle,
} from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  prospectOpportunities as opportunitiesApi,
  prospects as prospectsApi,
  quotes as quotesApi,
} from "@/api";
import CommercialScopeFilter from "@/components/CommercialScopeFilter";
import CommerceMetricCard from "@/components/commerce/CommerceMetricCard";
import ActaStandardPage from "@/components/layout/ActaStandardPage";
import { Button } from "@/components/ui/button";
import DirectQuoteModal from "@/components/DirectQuoteModal";
import OpportunityQuoteSubmitModal from "@/components/OpportunityQuoteSubmitModal";
import ShareModal from "@/components/ShareModal";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { THEME_COLORS } from "@/lib/themeColors";


// ACTA_AFFAIRES_WORKFLOW_COLUMNS_V18
const COLUMNS = [
  {
    key: "qualification",
    label: "À qualifier",
    description: "Nouveaux leads et contacts à traiter",
    color: THEME_COLORS.violet,
  },
  {
    key: "draft",
    label: "En cours",
    description: "Devis en préparation",
    color: THEME_COLORS.orange,
  },
  {
    key: "validation",
    label: "Devis à valider",
    description: "Soumis à l’administrateur",
    color: THEME_COLORS.warning,
  },
  {
    key: "ready",
    label: "À envoyer",
    description: "Validé par l’administrateur",
    color: THEME_COLORS.primary,
  },
  {
    key: "client",
    label: "Attente client",
    description: "Envoyé, négociation ou refus",
    color: THEME_COLORS.sky,
  },
  {
    key: "finalization",
    label: "Finalisation",
    description: "Devis accepté, paiement à valider",
    color: THEME_COLORS.teal,
  },
  {
    key: "won",
    label: "Gagnées",
    description: "Ventes finalisées",
    color: THEME_COLORS.success,
  },
  {
    key: "lost",
    label: "Perdues",
    description: "Affaires clôturées avec motif",
    color: THEME_COLORS.danger,
  },
];

const HIDDEN_PIPELINE_COLUMNS = new Set([
  "qualification",
  "draft",
]);

const PIPELINE_TOUCH_DRAG_DELAY = 360;
const PIPELINE_TOUCH_MOVE_TOLERANCE = 10;

const PAYMENT_PROOF_REQUIRED_METHODS = new Set([
  "bank_financing",
  "leasing",
  "third_party",
  "bank_transfer",
]);
function opportunityQuoteStatus(opportunity) {
  return (
    opportunity?.primaryQuoteStatus
    || opportunity?.quoteWorkflowStatus
    || ""
  );
}

function opportunityWorkflowColumn(opportunity) {
  const stage = opportunity?.stage || "opportunity";
  if (stage === "won") return "won";
  if (stage === "lost") return "lost";

  const quoteStatus = opportunityQuoteStatus(opportunity);
  if (["accepted", "purchase_order_created"].includes(quoteStatus)
      || stage === "accepted") {
    return "finalization";
  }
  if (["sent", "refused"].includes(quoteStatus)) return "client";
  if (quoteStatus === "approved") return "ready";
  if (quoteStatus === "pending_validation") return "validation";
  // Une opportunité créée est déjà une affaire commerciale.
  // Même sans devis, elle appartient donc à En cours.
  if (!opportunity?.primaryQuoteId) return "draft";
  return "draft";
}

// ACTA_COMMERCIAL_QUOTE_REVIEW_STATUS_V1
// Le statut métier reste inchangé : seule sa projection commerciale évolue.
function opportunityDisplayColumn(opportunity, isAdmin) {
  if (
    !isAdmin
    && opportunityQuoteStatus(opportunity) === "rejected"
  ) {
    return "validation";
  }

  return opportunityWorkflowColumn(opportunity);
}

function allowedPipelineDropStages(opportunity) {
  const currentColumn = opportunityWorkflowColumn(opportunity);
  const transitions = {
    qualification: ["draft", "lost"],
    draft: ["validation", "lost"],
    validation: ["draft", "ready", "lost"],
    ready: ["client", "lost"],
    client: ["draft", "finalization", "lost"],
    finalization: ["won", "lost"],
    won: [],
    lost: [opportunity?.primaryQuoteId ? "draft" : "qualification"],
  };
  return transitions[currentColumn] || [];
}

function pipelineDropRequirement(
  opportunity,
  nextStage,
  isAdmin
) {
  const currentColumn = opportunityWorkflowColumn(opportunity);
  const quoteStatus = opportunityQuoteStatus(opportunity);
  const quoteNumber = opportunity?.primaryQuoteNumber || "Le devis lié";

  if (nextStage === currentColumn) return null;

  if (currentColumn === "won") {
    return {
      blocked: true,
      title: "Affaire gagnée et figée",
      description:
        "Une réouverture nécessite une action administrative tracée.",
      actionType: "none",
      actionLabel: "",
    };
  }

  if (nextStage === "lost" && currentColumn !== "lost") {
    return {
      blocked: false,
      title: "Classer l’affaire comme perdue",
      description: "Un motif obligatoire sera demandé.",
      actionType: "move_lost",
      actionLabel: "Indiquer le motif",
    };
  }

  if (
    currentColumn === "lost"
    && ["qualification", "draft"].includes(nextStage)
  ) {
    return {
      blocked: false,
      title: "Réactiver l’affaire",
      description:
        "L’affaire revient dans son étape de préparation avec son historique conservé.",
      actionType: "reactivate_opportunity",
      actionLabel: "Réactiver",
    };
  }


  if (currentColumn === "qualification" && nextStage === "draft") {
    return {
      blocked: true,
      title: "Créer le devis",
      description: "Qualifiez l’affaire puis créez son premier devis.",
      actionType: "create_quote",
      actionLabel: "Créer le devis",
    };
  }

  if (currentColumn === "draft" && nextStage === "validation") {
    if (!opportunity?.primaryQuoteId) {
      return {
        blocked: true,
        title: "Créer le devis",
        description:
          "L’affaire ne possède pas encore de devis à soumettre.",
        actionType: "create_quote",
        actionLabel: "Créer le devis",
      };
    }
    if (["draft", "rejected"].includes(quoteStatus)) {
      return {
        blocked: true,
        title: "Soumettre à validation",
        description: `${quoteNumber} sera transmis à l’administrateur.`,
        actionType: "submit_quote",
        actionLabel: "Soumettre",
      };
    }
  }

  if (currentColumn === "validation" && nextStage === "draft") {
    return {
      blocked: true,
      title: "Demander une correction",
      description:
        "Le rejet administratif exige un motif et renvoie le devis en Brouillons.",
      actionType: isAdmin ? "reject_quote" : "open_affair",
      actionLabel: isAdmin ? "Indiquer le motif" : "Ouvrir l’affaire",
    };
  }

  if (currentColumn === "validation" && nextStage === "ready") {
    return {
      blocked: true,
      title: "Validation administrative",
      description:
        "Prévisualisez puis validez le devis avant tout envoi.",
      actionType: isAdmin ? "approve_quote" : "open_affair",
      actionLabel: isAdmin ? "Valider le devis" : "Ouvrir l’affaire",
    };
  }

  if (currentColumn === "ready" && nextStage === "client") {
    return {
      blocked: true,
      title: "Envoyer le devis",
      description:
        "Le partage sécurisé s’ouvre avant le passage en Attente client.",
      actionType: "send_quote",
      actionLabel: "Envoyer",
    };
  }

  if (currentColumn === "client" && nextStage === "draft") {
    return {
      blocked: true,
      title: "Créer une nouvelle version",
      description:
        "La version envoyée reste figée et une révision revient en Brouillons.",
      actionType: "create_revision",
      actionLabel: "Nouvelle version",
    };
  }

  if (currentColumn === "client" && nextStage === "finalization") {
    return {
      blocked: true,
      title: "Enregistrer l’acceptation",
      description:
        "Confirmez l’accord client avant la finalisation financière.",
      actionType: "accept_quote",
      actionLabel: "Confirmer l’acceptation",
    };
  }

  if (currentColumn === "finalization" && nextStage === "won") {
    return {
      blocked: true,
      title: "Finalisation administrative",
      description:
        "Contrôlez devis, financement applicable et paiement avant clôture.",
      actionType: "open_finalization",
      actionLabel: isAdmin ? "Contrôler et finaliser" : "Voir les contrôles",
    };
  }

  return {
    blocked: true,
    title: "Transition non disponible",
    description:
      "Respectez le workflow Devis, Client et Finalisation.",
    actionType: "none",
    actionLabel: "",
  };
}

function getPipelineColumnPageSize(
  viewportHeight = 900,
  viewportWidth = 1440,
) {
  if (viewportWidth <= 900) return 2;
  if (viewportHeight < 820) return 2;
  return 3;
}

const WORKFLOW_BADGES = {
  qualification: {
    label: "À qualifier",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  draft: {
    label: "En cours",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  validation: {
    label: "À valider",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  ready: {
    label: "Validé",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  client: {
    label: "Client",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  finalization: {
    label: "Finalisation",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  won: {
    label: "Gagné",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
  lost: {
    label: "Perdu",
    className: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  },
};

function opportunityStageBadge(opportunity) {
  const column = opportunityWorkflowColumn(opportunity);
  return WORKFLOW_BADGES[column] || WORKFLOW_BADGES.draft;
}


function safeArray(value) {
  return Array.isArray(value) ? value : [];
}


function safeNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}


const PIPELINE_AMOUNT_ANOMALY_LIMIT = 1_000_000_000_000;


function pipelineAmount(value) {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return 0;
  }

  const number = Number(value);

  if (
    !Number.isFinite(number)
    || number < 0
    || number >= PIPELINE_AMOUNT_ANOMALY_LIMIT
  ) {
    return null;
  }

  return number;
}


function amountForTotal(value) {
  return pipelineAmount(value) ?? 0;
}


function pipelineHasAmountAnomaly(value) {
  return pipelineAmount(value) === null;
}


function formatCurrency(value) {
  const number = pipelineAmount(value);

  if (number === null) {
    return "Montant à vérifier";
  }

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(number);
}


function formatActionDate(value) {
  if (!value) return "";

  const raw = String(value);
  const date = new Date(
    raw.length === 10
      ? `${raw}T12:00:00`
      : raw
  );

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compared = new Date(date);
  compared.setHours(0, 0, 0, 0);
  const dayDiff = Math.round(
    (compared.getTime() - today.getTime())
    / 86_400_000
  );

  if (dayDiff === 0) return "Aujourd’hui";
  if (dayDiff === 1) return "Demain";
  if (dayDiff === -1) return "Hier";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
  }).format(date);
}


function pipelineOwnerLabel(opportunity) {
  return (
    opportunity?.ownerName
    || opportunity?.assignedUserName
    || opportunity?.createdByName
    || "Non assigné"
  );
}


function pipelineIsOverdue(opportunity) {
  const stage = pipelineItemStage(opportunity);

  if (["won", "lost"].includes(stage)) {
    return false;
  }

  if (!opportunity?.nextActionAt) {
    return false;
  }

  const date = new Date(opportunity.nextActionAt);

  return (
    !Number.isNaN(date.getTime())
    && date.getTime() < Date.now()
  );
}


const PIPELINE_PERIOD_MODES = [
  { value: "before", label: "Avant une date" },
  { value: "after", label: "Après une date" },
  { value: "between", label: "Entre deux dates" },
];

function toDateKey(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


// ACTA_AFFAIRES_COMPACT_UI_V39
// ACTA_AFFAIRES_FINAL_ALIGNMENT_V40
// ACTA_AFFAIRES_ACCESSIBLE_TOOLBAR_V41
const PIPELINE_DESKTOP_PERIOD_PRESETS = [
  ["today", "Aujourd’hui"],
  ["this-month", "Ce mois"],
  ["last-3-months", "3 mois"],
  ["last-6-months", "6 mois"],
  ["current-year", "Année"],
];

function pipelineDesktopPeriodRange(preset) {
  const today = new Date();
  const todayKey = toDateKey(today);

  if (preset === "today") {
    return { from: todayKey, to: todayKey };
  }

  if (preset === "this-month") {
    return {
      from: toDateKey(
        new Date(today.getFullYear(), today.getMonth(), 1)
      ),
      to: toDateKey(
        new Date(today.getFullYear(), today.getMonth() + 1, 0)
      ),
    };
  }

  if (preset === "last-3-months" || preset === "last-6-months") {
    const from = new Date(today);
    from.setMonth(
      from.getMonth() - (preset === "last-3-months" ? 3 : 6)
    );

    return {
      from: toDateKey(from),
      to: todayKey,
    };
  }

  if (preset === "current-year") {
    return {
      from: `${today.getFullYear()}-01-01`,
      to: `${today.getFullYear()}-12-31`,
    };
  }

  return { from: todayKey, to: todayKey };
}


function pipelineItemType(item) {
  return item?.pipelineItemType || "opportunity";
}

function pipelineItemStage(item) {
  if (pipelineItemType(item) !== "opportunity") {
    return item?.stage || "";
  }
  return opportunityWorkflowColumn(item);
}

function allowedPipelineItemDropStages(item) {
  if (pipelineItemType(item) !== "opportunity") {
    return [];
  }
  return allowedPipelineDropStages(item);
}

function pipelineItemDropRequirement(
  item,
  nextStage,
  isAdmin
) {
  if (pipelineItemType(item) !== "opportunity") {
    return null;
  }
  return pipelineDropRequirement(
    item,
    nextStage,
    isAdmin
  );
}

function opportunitySortValue(opportunity) {
  if (!opportunity?.expectedCloseDate) return 9999999999999;

  const date = new Date(
    String(opportunity.expectedCloseDate).length === 10
      ? `${opportunity.expectedCloseDate}T12:00:00`
      : opportunity.expectedCloseDate
  );

  if (Number.isNaN(date.getTime())) {
    return 9999999999999;
  }

  return date.getTime();
}


function PipelineDraggableCard({
  item,
  moving,
  onOpen,
  testId,
  className = "",
  children,
}) {
  const suppressClickRef = useRef(false);

  return (
    <article
      className={`pipeline-final-card ${className}`}
      data-testid={testId}
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }

        if (!moving) {
          onOpen?.(item);
        }
      }}
      onKeyDown={(event) => {
        if (
          event.key === "Enter"
          || event.key === " "
        ) {
          event.preventDefault();
          onOpen?.(item);
        }
      }}
      role="button"
      tabIndex={0}
      aria-grabbed={false}
    >
      {children}
    </article>
  );
}


function OpportunityLostModal({
  opportunity,
  reason,
  saving,
  onReasonChange,
  onClose,
  onSubmit,
}) {
  if (!opportunity) return null;

  return (
    <div
      className="acta-modal-overlay fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget
          && !saving
        ) {
          onClose();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="opportunity-lost-title"
        className="acta-modal-surface acta-modal-scroll max-h-[92dvh] w-full max-w-lg rounded-3xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-6"
        data-testid="pipeline-opportunity-lost-modal"
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--do-border)] pb-4">
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-[0.12em] text-[var(--do-accent)]">
              Opportunité perdue
            </div>

            <h2
              id="opportunity-lost-title"
              className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]"
            >
              Indiquer le motif de perte
            </h2>

            <p className="mt-1 truncate text-sm text-[var(--do-muted)]">
              {opportunity.title || "Besoin commercial"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)] disabled:opacity-50"
            aria-label="Fermer"
          >
            <XCircle
              size={20}
              weight="bold"
            />
          </button>
        </div>

        <form
          className="mt-5"
          onSubmit={onSubmit}
        >
          <label className="block">
            <span className="text-sm font-black text-[var(--do-ink)]">
              Motif obligatoire
            </span>

            <textarea
              required
              autoFocus
              rows={5}
              maxLength={1000}
              value={reason}
              onChange={(event) =>
                onReasonChange(
                  event.target.value
                )
              }
              placeholder="Ex. budget insuffisant, choix d’un concurrent, projet annulé…"
              className="mt-2 min-h-[130px] w-full resize-none rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-base text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)] sm:text-sm"
              data-testid="pipeline-opportunity-lost-reason"
            />

            <span className="mt-1 block text-right text-xs text-[var(--do-muted)]">
              {reason.length}/1000
            </span>
          </label>

          <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs leading-5 text-[var(--do-ink)]">
            L’opportunité restera dans l’historique du contact avec son motif de perte.
          </div>

          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="min-h-[46px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                !reason.trim()
              }
              className="min-h-[46px] rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] transition hover:bg-[var(--do-accent-strong)] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="pipeline-opportunity-lost-confirm"
            >
              {saving
                ? "Enregistrement…"
                : "Confirmer la perte"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}


function PipelineDropActionModal({
  action,
  saving,
  onClose,
  onConfirm,
}) {
  if (!action) return null;

  return (
    <div
      className="acta-modal-overlay fixed inset-0 z-[135] flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        className="acta-modal-surface w-full max-w-md rounded-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]"
        data-testid="pipeline-drop-action-modal"
      >
        <div
          className={`rounded-2xl border p-4 ${
            action.requirement.blocked
              ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
              : "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
          }`}
        >
          <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">
            {action.requirement.title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-[var(--do-muted)]">
            {action.requirement.description}
          </p>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
          >
            Fermer
          </button>

          {action.requirement.actionType !== "none" && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={saving}
              className="min-h-[44px] rounded-xl bg-[var(--do-accent-strong)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="pipeline-drop-action-cta"
            >
              {action.requirement.actionLabel}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}


function OpportunityCard({
  opportunity,
  moving,
  onOpen,
  onSend,
  onPreview,
}) {
  const stageBadge = opportunityStageBadge(opportunity);
  const item = {
    ...opportunity,
    pipelineItemType: "opportunity",
  };
  const quoteStatus = opportunityQuoteStatus(opportunity);
  const quoteLabel = opportunity.primaryQuoteId
    ? `${opportunity.primaryQuoteNumber || "Devis"}${
        opportunity.primaryQuoteVersion
          ? ` · V${opportunity.primaryQuoteVersion}`
          : ""
      }`
    : "Sans devis";
  const paymentStatus =
    opportunity.payment?.status || "missing";
  const workflowColumn =
    opportunityWorkflowColumn(opportunity);
  const amountAnomaly = pipelineHasAmountAnomaly(
    opportunity.estimatedAmount
  );
  const overdue = pipelineIsOverdue(opportunity);
  const ownerLabel = pipelineOwnerLabel(opportunity);
  const referenceLabel = (
    opportunity.primaryQuoteNumber
    || opportunity.reference
    || "Sans référence"
  );

  let footerLabel = quoteLabel;
  let footerClass = opportunity.primaryQuoteId
    ? "is-quote"
    : "is-warning";

  if (quoteStatus === "pending_validation") {
    footerLabel = "En attente admin";
    footerClass = "is-warning";
  } else if (quoteStatus === "rejected") {
    footerLabel = "Modification demandée";
    footerClass = "is-lost";
  } else if (quoteStatus === "approved") {
    footerLabel = "À envoyer";
    footerClass = "is-quote";
  } else if (quoteStatus === "sent") {
    footerLabel = "Envoyé";
    footerClass = "is-quote";
  } else if (quoteStatus === "refused") {
    footerLabel = "Refus client";
    footerClass = "is-lost";
  } else if (workflowColumn === "finalization") {
    footerLabel =
      paymentStatus === "validated"
        ? "Paiement validé"
        : paymentStatus === "rejected"
          ? "Paiement rejeté"
          : "Paiement à valider";
    footerClass =
      paymentStatus === "validated"
        ? "is-success"
        : paymentStatus === "rejected"
          ? "is-lost"
          : "is-warning";
  }

  let activityLabel =
    opportunity.nextAction
    || "";
  let activityDate =
    formatActionDate(opportunity.nextActionAt);
  let activityTone = overdue
    ? "is-overdue"
    : opportunity.nextAction
      ? ""
      : "is-missing";

  if (workflowColumn === "won") {
    activityLabel = "Vente finalisée";
    activityDate = formatActionDate(
      opportunity.primaryQuoteSaleFinalizedAt
      || opportunity.wonAt
      || opportunity.updatedAt
    );
    activityTone = "is-done";
  } else if (workflowColumn === "lost") {
    activityLabel =
      opportunity.lostReason
      || "Affaire clôturée";
    activityDate = formatActionDate(
      opportunity.lostAt
      || opportunity.updatedAt
    );
    activityTone = "is-lost";
  }

  return (
    <PipelineDraggableCard
      item={item}
      moving={moving}
      onOpen={onOpen}
      testId="pipeline-opportunity-card"
      className={
        opportunity.stage === "lost"
          ? "is-lost"
          : ""
      }
    >
      <div className="pipeline-premium-card-head">
        <div className="min-w-0 flex-1">
          <strong className="pipeline-final-card-title">
            {opportunity.title || "Affaire commerciale"}
          </strong>
          <span className="pipeline-final-card-subtitle">
            {opportunity.prospectCompany
              || opportunity.prospectName
              || "Contact lié"}
          </span>
        </div>

        <span
          className={`pipeline-final-stage-badge ${stageBadge.className}`}
        >
          {stageBadge.label}
        </span>
      </div>

      <div className="pipeline-premium-card-value">
        <strong
          className={`pipeline-final-card-amount ${
            amountAnomaly ? "is-anomaly" : ""
          }`}
        >
          {formatCurrency(opportunity.estimatedAmount)}
        </strong>

        <span className={`pipeline-final-badge ${footerClass}`}>
          {footerLabel}
        </span>
      </div>

      {quoteStatus === "rejected"
        && opportunity.primaryQuoteRejectionReason && (
          <div
            className="mt-3 line-clamp-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-3 py-2 text-xs text-[var(--do-ink)]"
            data-testid="pipeline-quote-correction-reason"
          >
            <strong>Modification demandée :</strong>{" "}
            {opportunity.primaryQuoteRejectionReason}
          </div>
        )}

      {activityLabel && (
        <div
          className={`pipeline-premium-next-action ${activityTone}`}
        >
          <span className="pipeline-premium-action-dot" />
          <span className="pipeline-premium-action-label">
            {activityLabel}
          </span>
          {activityDate && (
            <strong>{activityDate}</strong>
          )}
        </div>
      )}

      <footer className="pipeline-premium-card-meta">
        <span>{referenceLabel}</span>
        <span aria-hidden="true">•</span>
        <span>{ownerLabel}</span>
      </footer>

      {workflowColumn === "ready" && (
        <div
          className="mt-3 grid grid-cols-2 gap-2"
          data-testid="pipeline-ready-actions"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSend?.(opportunity);
            }}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-3 text-sm font-black text-[var(--do-on-accent)] transition hover:bg-[var(--do-accent-strong)]"
            data-testid="pipeline-ready-send-action"
          >
            <PaperPlaneTilt size={17} weight="bold" />
            Envoyer
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onPreview?.(opportunity);
            }}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm font-black text-[var(--do-ink)] transition hover:bg-[var(--do-surface-soft)]"
            data-testid="pipeline-ready-preview-action"
            aria-label="Voir le devis"
          >
            <Eye size={17} weight="bold" />
            Voir
          </button>
        </div>
      )}
    </PipelineDraggableCard>
  );
}


export default function CommercePipeline() {
  const { user } = useAuth();
  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const canFilterCommercials = [
    "admin",
    "manager",
  ].includes(user?.role);
  const [board, setBoard] = useState({
    newProspects: [],
    opportunities: [],
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileFilterSheet, setMobileFilterSheet] = useState("");
  const [mobileFilterSubMenu, setMobileFilterSubMenu] = useState("");

  const [loadError, setLoadError] = useState("");
  const hasLoadedBoardRef = useRef(false);
  const boardRequestIdRef = useRef(0);
  const [movingId, setMovingId] = useState("");
  const [pipelineDropAction, setPipelineDropAction] = useState(null);
  const [pipelineFilterValue, setPipelineFilterValue] = useState("all");
  const [pipelinePeriodMode, setPipelinePeriodMode] = useState("before");
  const [pipelinePeriodDateFrom, setPipelinePeriodDateFrom] = useState("");
  const [pipelinePeriodDateTo, setPipelinePeriodDateTo] = useState(
    () => toDateKey(new Date())
  );
  const [pipelineStageFilter, setPipelineStageFilter] = useState("all");
  const [pipelineCommercialUsers, setPipelineCommercialUsers] = useState([]);
  const [
    pipelineDesktopPeriodPreset,
    setPipelineDesktopPeriodPreset,
  ] = useState("custom");
  const [
    pipelineDesktopCustomOpen,
    setPipelineDesktopCustomOpen,
  ] = useState(false);
  const touchDragRef = useRef({
    active: false,
    item: null,
    pointerId: null,
    startX: 0,
    startY: 0,
  });
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [archiveConfirmOpportunity, setArchiveConfirmOpportunity] = useState(null);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [shareOpportunity, setShareOpportunity] = useState(null);
  const [paymentDraft, setPaymentDraft] = useState({ method: "", provider: "", amount: "", reference: "", quoteId: "", proofDocumentId: "", proofType: "", comment: "" });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [proofUploading, setProofUploading] = useState(false);
  const [finalizationSubmitting, setFinalizationSubmitting] = useState(false);
  const [quoteOpportunity, setQuoteOpportunity] = useState(null);
  const [directQuoteModalOpen, setDirectQuoteModalOpen] = useState(false);
  const [directQuoteProspect, setDirectQuoteProspect] = useState(null);
  const [quoteActionSaving, setQuoteActionSaving] = useState(false);
  const [quoteRejectReason, setQuoteRejectReason] = useState("");
  const [quoteValidationPdfUrl, setQuoteValidationPdfUrl] = useState("");
  const [quoteValidationPdfLoading, setQuoteValidationPdfLoading] = useState(false);
  const [quoteValidationPdfError, setQuoteValidationPdfError] = useState("");
  const [financingDecisionReason, setFinancingDecisionReason] = useState("");
  const [financingProviderDraft, setFinancingProviderDraft] = useState("");
  const [financingSaving, setFinancingSaving] = useState(false);
  const [purchaseOrderSaving, setPurchaseOrderSaving] = useState(false);
  const [finalizingSale, setFinalizingSale] = useState(false);
  const [evolizExporting, setEvolizExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [columnPages, setColumnPages] = useState({});
  const [pipelineViewport, setPipelineViewport] = useState(() => ({
    width:
      typeof window === "undefined"
        ? 1440
        : window.innerWidth,
    height:
      typeof window === "undefined"
        ? 900
        : window.innerHeight,
  }));
  const pipelinePageSize = getPipelineColumnPageSize(
    pipelineViewport.height,
    pipelineViewport.width,
  );
  const [openMobileColumn, setOpenMobileColumn] = useState("opportunity");
  const [
    lostOpportunity,
    setLostOpportunity,
  ] = useState(null);

  const [
    lostOpportunityReason,
    setLostOpportunityReason,
  ] = useState("");

  const openOpportunityModal = () => {
    setDirectQuoteModalOpen(true);
  };

  // ACTA_PIPELINE_DASHBOARD_NEW_OPPORTUNITY_ENTRY_V1
  useEffect(() => {
    if (
      searchParams.get("action")
      !== "new-opportunity"
    ) {
      return;
    }

    setDirectQuoteModalOpen(true);

    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.delete("action");

    setSearchParams(
      nextSearchParams,
      {
        replace: true,
      }
    );
  }, [
    searchParams,
    setSearchParams,
  ]);


  useEffect(() => {
    if (searchParams.get("action") !== "new-quote") return undefined;
    const contactId = searchParams.get("contactId") || "";
    if (!contactId) {
      toast.error("Contact à préremplir introuvable");
      return undefined;
    }
    let cancelled = false;
    prospectsApi.activity(contactId)
      .then((payload) => {
        if (cancelled) return;
        const contact = payload?.prospect || payload;
        if (!contact?.id) throw new Error("Contact à préremplir introuvable");
        setDirectQuoteProspect(contact);
        setDirectQuoteModalOpen(true);
      })
      .catch((error) => {
        if (!cancelled) toast.error(formatApiError(error.response?.data?.detail) || error.message || "Impossible de préparer le devis");
      });
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("action");
    nextSearchParams.delete("contactId");
    setSearchParams(nextSearchParams, { replace: true });
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const updateViewport = () => {
      setPipelineViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  const applyDesktopPeriodPreset = useCallback((preset) => {
    const range = pipelineDesktopPeriodRange(preset);

    setPipelinePeriodMode("between");
    setPipelinePeriodDateFrom(range.from);
    setPipelinePeriodDateTo(range.to);
    setPipelineDesktopPeriodPreset(preset);
    setPipelineDesktopCustomOpen(false);
  }, []);


  const applyPipelinePeriodMode = useCallback((mode) => {
    setPipelinePeriodMode(mode);
    if (mode === "before") {
      setPipelinePeriodDateFrom("");
      setPipelinePeriodDateTo((v) => v || toDateKey(new Date()));
    } else if (mode === "after") {
      setPipelinePeriodDateFrom((v) => v || toDateKey(new Date()));
      setPipelinePeriodDateTo("");
    } else {
      setPipelinePeriodDateFrom((v) => v || toDateKey(new Date()));
      setPipelinePeriodDateTo((v) => v || toDateKey(new Date()));
    }
  }, []);

  useEffect(() => {
    if (!canFilterCommercials) {
      setPipelineCommercialUsers([]);
      setPipelineFilterValue("all");
      return undefined;
    }

    let cancelled = false;

    prospectsApi
      .listCommercialUsers()
      .then((rows) => {
        if (cancelled) return;
        setPipelineCommercialUsers(
          safeArray(rows).filter(
            (entry) =>
              entry?.id
              && ["admin", "manager", "user"].includes(entry.role)
          )
        );
      })
      .catch(() => {
        if (!cancelled) setPipelineCommercialUsers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [canFilterCommercials]);

  const pipelineCommercialOptions = useMemo(() => {
    const allLabel =
      user?.role === "manager"
        ? "Mon équipe"
        : "Tous les commerciaux";
    const currentUserId = String(user?.id || "");

    return [
      {
        key: "all",
        value: "all",
        label: allLabel,
      },
      ...(currentUserId
        ? [
            {
              key: "self",
              value: "self",
              label: "Moi — utilisateur en cours",
            },
          ]
        : []),
      ...pipelineCommercialUsers
        .filter(
          (entry) =>
            entry?.id
            && String(entry.id) !== currentUserId
        )
        .map((entry) => ({
          key: `owner-${entry.id}`,
          value: `owner:${entry.id}`,
          label: entry.name,
        })),
    ];
  }, [
    pipelineCommercialUsers,
    user?.id,
    user?.role,
  ]);

  const pipelineFilterRequest = useMemo(() => {
    const request = {
      dateFrom: pipelinePeriodDateFrom,
      dateTo: pipelinePeriodDateTo,
    };

    if (
      !canFilterCommercials
      || pipelineFilterValue === "all"
    ) {
      return request;
    }

    if (pipelineFilterValue === "self") {
      request.ownerUserId = user?.id || "";
    } else if (pipelineFilterValue.startsWith("owner:")) {
      request.ownerUserId = pipelineFilterValue.slice(6);
    } else if (pipelineFilterValue.startsWith("role:")) {
      request.ownerRole = pipelineFilterValue.slice(5);
    }

    return request;
  }, [
    canFilterCommercials,
    pipelineFilterValue,
    pipelinePeriodDateFrom,
    pipelinePeriodDateTo,
    user?.id,
  ]);

  const loadBoard = useCallback(async () => {
    const requestId = boardRequestIdRef.current + 1;
    boardRequestIdRef.current = requestId;
    const isInitialLoad = !hasLoadedBoardRef.current;

    if (isInitialLoad) {
      setLoading(true);
      setLoadError("");
    } else {
      setRefreshing(true);
    }

    try {
      const data = await opportunitiesApi.pipelineBoard(
        pipelineFilterRequest
      );

      if (requestId !== boardRequestIdRef.current) return;

      setBoard({
        newProspects: safeArray(data.newProspects),
        opportunities: safeArray(data.opportunities),
      });
      hasLoadedBoardRef.current = true;
      setLoadError("");
    } catch (error) {
      if (requestId !== boardRequestIdRef.current) return;

      const message =
        formatApiError(error.response?.data?.detail)
        || "Impossible de charger les affaires.";

      if (isInitialLoad) {
        setLoadError(message);
      }
      toast.error(message);
    } finally {
      if (requestId === boardRequestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [pipelineFilterRequest]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    setColumnPages({});
  }, [
    search,
    pipelinePageSize,
    pipelineFilterValue,
    pipelinePeriodDateFrom,
    pipelinePeriodDateTo,
    pipelinePeriodMode,
    pipelineStageFilter,
  ]);

  const filteredOpportunities = useMemo(() => {
    const needle = search
      .trim()
      .toLocaleLowerCase("fr-FR");

    return board.opportunities.filter((opportunity) => {
      if (
        pipelineStageFilter !== "all"
        && opportunityWorkflowColumn(opportunity)
          !== pipelineStageFilter
      ) {
        return false;
      }

      if (!needle) return true;

      return [
        opportunity.title,
        opportunity.machineName,
        opportunity.description,
        opportunity.prospectName,
        opportunity.prospectCompany,
        opportunity.prospectEmail,
        opportunity.primaryQuoteNumber,
        opportunity.reference,
        opportunity.ownerName,
        opportunity.nextAction,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("fr-FR")
        .includes(needle);
    });
  }, [
    board.opportunities,
    pipelineStageFilter,
    search,
  ]);

  const grouped = useMemo(() => {
    const groups = Object.fromEntries(
      COLUMNS
  .filter((column) => !HIDDEN_PIPELINE_COLUMNS.has(column.key))
  .map((column) => [column.key, []])
    );

    filteredOpportunities.forEach((item) => {
      const columnKey = opportunityDisplayColumn(
        item,
        isAdmin
      );

      if (groups[columnKey]) {
        groups[columnKey].push(item);
      }
    });

    Object.values(groups).forEach((items) => {
      items.sort((first, second) => {
        const dateDiff =
          opportunitySortValue(first)
          - opportunitySortValue(second);

        if (dateDiff !== 0) return dateDiff;

        return (
          amountForTotal(second.estimatedAmount)
          - amountForTotal(first.estimatedAmount)
        );
      });
    });

    return groups;
  }, [filteredOpportunities, isAdmin]);

  const visibleColumns = useMemo(() => {
    const columns = COLUMNS.filter(
      (column) =>
        !HIDDEN_PIPELINE_COLUMNS.has(column.key)
        && (
          pipelineStageFilter === "all"
          || column.key === pipelineStageFilter
        )
    );

    return columns.map((column) => {
      if (column.key !== "validation" || isAdmin) {
        return column;
      }

      return {
        ...column,
        label: "Devis en attente",
        description: "Décision administrateur",
      };
    });
  }, [isAdmin, pipelineStageFilter]);

  useEffect(() => {
    if (
      pipelineStageFilter !== "all" &&
      !visibleColumns.some(
        (column) => column.key === pipelineStageFilter
      )
    ) {
      setPipelineStageFilter("all");
    }
  }, [pipelineStageFilter, visibleColumns]);

  useEffect(() => {
    if (
      openMobileColumn &&
      !visibleColumns.some(
        (column) => column.key === openMobileColumn
      )
    ) {
      setOpenMobileColumn("");
    }
  }, [openMobileColumn, visibleColumns]);

  const toggleMobileColumn = useCallback((columnKey) => {
    setOpenMobileColumn((current) =>
      current === columnKey ? "" : columnKey
    );
  }, []);

  const selectedCommercialLabel = useMemo(() => (
    pipelineCommercialOptions.find(
      (option) => option.value === pipelineFilterValue
    )?.label
    || "Filtre actif"
  ), [
    pipelineCommercialOptions,
    pipelineFilterValue,
  ]);

  const selectedPeriodLabel = useMemo(() => {
    if (pipelinePeriodMode === "before") {
      return pipelinePeriodDateTo ? `Avant le ${pipelinePeriodDateTo}` : "Avant une date";
    }
    if (pipelinePeriodMode === "after") {
      return pipelinePeriodDateFrom ? `Après le ${pipelinePeriodDateFrom}` : "Après une date";
    }
    return pipelinePeriodDateFrom && pipelinePeriodDateTo
      ? `${pipelinePeriodDateFrom} → ${pipelinePeriodDateTo}`
      : "Entre deux dates";
  }, [pipelinePeriodDateFrom, pipelinePeriodDateTo, pipelinePeriodMode]);

  const metrics = useMemo(() => {
    const activeColumns = new Set([
      "validation",
      "ready",
      "client",
      "finalization",
    ]);

    const forecastColumns = new Set([
      "ready",
      "client",
      "finalization",
    ]);

    const active = board.opportunities.filter((item) =>
      activeColumns.has(
        opportunityWorkflowColumn(item)
      )
    );

    const forecast = board.opportunities.filter((item) =>
      forecastColumns.has(
        opportunityWorkflowColumn(item)
      )
    );

    const won = board.opportunities.filter((item) =>
      opportunityWorkflowColumn(item) === "won"
    );

    return {
      newProspects: board.newProspects.length,
      active: active.length,
      forecast: forecast.reduce(
        (sum, item) =>
          sum + amountForTotal(item.estimatedAmount),
        0
      ),
      won: won.reduce(
        (sum, item) =>
          sum + amountForTotal(item.estimatedAmount),
        0
      ),
    };
  }, [board]);

  const executeOpportunityMove = async (
    opportunity,
    nextStage,
    lostReason = "",
  ) => {
    setMovingId(opportunity.id);

    try {
      await opportunitiesApi.moveStage(
        opportunity.id,
        {
          stage: nextStage,
          lostReason:
            lostReason.trim(),
        }
      );

      toast.success(
        nextStage === "lost"
          ? "Opportunité classée comme perdue."
          : "Besoin déplacé."
      );

      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Impossible de déplacer le besoin."
      );
    } finally {
      setMovingId("");
    }
  };


  const moveOpportunity = async (
    opportunity,
    nextStage
  ) => {
    if (!nextStage || movingId) return;

    if (nextStage === "lost") {
      setLostOpportunity(opportunity);
      setLostOpportunityReason("");
      return;
    }

    if (nextStage === "opportunity") {
      await executeOpportunityMove(
        opportunity,
        "opportunity"
      );
    }
  };


  const closeLostOpportunityModal = () => {
    if (movingId) return;

    setLostOpportunity(null);
    setLostOpportunityReason("");
  };


  const submitLostOpportunity = async (
    event
  ) => {
    event.preventDefault();

    const cleanReason =
      lostOpportunityReason.trim();

    if (
      !lostOpportunity ||
      movingId
    ) {
      return;
    }

    if (!cleanReason) {
      toast.error(
        "Motif obligatoire pour passer un besoin en perdu."
      );
      return;
    }

    const opportunity =
      lostOpportunity;

    await executeOpportunityMove(
      opportunity,
      "lost",
      cleanReason
    );

    setLostOpportunity(null);
    setLostOpportunityReason("");
  };


  const setColumnPage = useCallback((columnKey, nextPage) => {
    setColumnPages((previous) => ({
      ...previous,
      [columnKey]: Math.max(1, nextPage),
    }));
  }, []);

  useEffect(() => {
    const payment = selectedOpportunity?.payment || {};
    setPaymentDraft({
      method: payment.method || "", provider: payment.provider || "", amount: payment.amount ?? "",
      reference: payment.reference || "", quoteId: payment.quoteId || selectedOpportunity?.primaryQuoteId || "",
      proofDocumentId: payment.proofDocumentId || "", proofType: payment.proofType || "",
      comment: payment.comment || "", rejectionReasonCode: payment.rejectionReasonCode || "",
      rejectionReason: payment.rejectionReason || "", source: payment.source || "human",
    });
  }, [selectedOpportunity]);

  useEffect(() => {
    setFinancingProviderDraft(
      selectedOpportunity?.primaryQuoteFinancingProvider
      || selectedOpportunity?.payment?.provider
      || ""
    );
    setFinancingDecisionReason("");
    setQuoteRejectReason("");
  }, [selectedOpportunity]);

  const selectedOpportunityNeedsQuoteValidation = Boolean(
    isAdmin
    && selectedOpportunity?.primaryQuoteId
    && opportunityQuoteStatus(selectedOpportunity) === "pending_validation"
  );

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    setQuoteValidationPdfUrl("");
    setQuoteValidationPdfError("");

    if (!selectedOpportunityNeedsQuoteValidation) {
      setQuoteValidationPdfLoading(false);
      return undefined;
    }

    setQuoteValidationPdfLoading(true);

    quotesApi
      .downloadPdf(selectedOpportunity.primaryQuoteId)
      .then((blob) => {
        if (!active) return;

        objectUrl = URL.createObjectURL(blob);
        setQuoteValidationPdfUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;

        setQuoteValidationPdfError(
          "La prévisualisation du devis est indisponible. Fermez puis réessayez."
        );
      })
      .finally(() => {
        if (active) {
          setQuoteValidationPdfLoading(false);
        }
      });

    return () => {
      active = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    selectedOpportunity?.primaryQuoteId,
    selectedOpportunityNeedsQuoteValidation,
  ]);

  const previewOpportunityQuote = useCallback(async (opportunity) => {
    if (!opportunity?.primaryQuoteId) {
      toast.error("Aucun devis à prévisualiser.");
      return;
    }

    try {
      const blob = await quotesApi.downloadPdf(
        opportunity.primaryQuoteId
      );
      const objectUrl = URL.createObjectURL(blob);
      window.open(
        objectUrl,
        "_blank",
        "noopener,noreferrer"
      );
      window.setTimeout(
        () => URL.revokeObjectURL(objectUrl),
        60000
      );
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Prévisualisation du devis impossible."
      );
    }
  }, []);

  const previewSelectedQuote = useCallback(
    () => previewOpportunityQuote(selectedOpportunity),
    [
      previewOpportunityQuote,
      selectedOpportunity,
    ]
  );

  const exportSelectedQuoteToEvoliz = async () => {
    if (
      !isAdmin
      || !selectedOpportunity?.primaryQuoteId
      || opportunityQuoteStatus(selectedOpportunity) !== "accepted"
      || evolizExporting
    ) {
      return;
    }

    setEvolizExporting(true);

    try {
      const blob = await quotesApi.exportEvoliz(
        selectedOpportunity.primaryQuoteId
      );
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const quoteNumber =
        selectedOpportunity.primaryQuoteNumber
        || selectedOpportunity.primaryQuoteId
        || "devis";

      link.href = objectUrl;
      link.download = `${quoteNumber}_Evoliz.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);

      toast.success("Export Evoliz généré.");
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Export Evoliz impossible."
      );
    } finally {
      setEvolizExporting(false);
    }
  };

  const submitFinalization = async () => {
    if (!selectedOpportunity || finalizationSubmitting) return;
    if (!paymentDraft.method) {
      toast.error("Choisissez le type de financement ou le mode de paiement.");
      return;
    }

    setFinalizationSubmitting(true);
    try {
      const result = await opportunitiesApi.submitFinalization(
        selectedOpportunity.id,
        {
          ...paymentDraft,
          quoteId: selectedOpportunity.primaryQuoteId,
          status: "pending_review",
        }
      );
      const next = {
        ...selectedOpportunity,
        payment: result.payment,
      };
      setSelectedOpportunity(next);
      setBoard((current) => ({
        ...current,
        opportunities: current.opportunities.map((item) =>
          item.id === next.id ? next : item
        ),
      }));
      toast.success("Dossier envoyé à l’administrateur pour validation.");
      setSelectedOpportunity(null);
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Envoi du dossier de finalisation impossible."
      );
    } finally {
      setFinalizationSubmitting(false);
    }
  };

  const approveFinalization = async () => {
    if (!selectedOpportunity || finalizationSubmitting) return;
    setFinalizationSubmitting(true);
    try {
      await opportunitiesApi.validateOpportunityPayment(
        selectedOpportunity.id,
        {
          ...paymentDraft,
          decision: "validate",
          status: "validated",
        }
      );

      await quotesApi.finalizeSale(
        selectedOpportunity.primaryQuoteId,
        { prepareGmao: true }
      );

      toast.success("Dossier validé. L’affaire est gagnée.");
      setSelectedOpportunity(null);
      setOpenMobileColumn("won");
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Validation finale impossible."
      );
    } finally {
      setFinalizationSubmitting(false);
    }
  };

  const reviewPayment = async (decision) => {
    if (!selectedOpportunity || paymentSaving) return;
    setPaymentSaving(true);
    try {
      const result = await opportunitiesApi.validateOpportunityPayment(selectedOpportunity.id, { ...paymentDraft, decision, status: decision === "validate" ? "validated" : "rejected" });
      const next = { ...selectedOpportunity, payment: result.payment };
      setSelectedOpportunity(next);
      setBoard((current) => ({ ...current, opportunities: current.opportunities.map((item) => item.id === next.id ? next : item) }));
      toast.success(decision === "validate" ? "Paiement validé." : "Paiement refusé.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Validation du paiement impossible.");
    } finally { setPaymentSaving(false); }
  };

  const uploadPaymentProof = async (file) => {
    if (
      !selectedOpportunity
      || !file
      || proofUploading
    ) {
      return;
    }

    setProofUploading(true);

    try {
      const result =
        await opportunitiesApi.uploadPaymentProof(
          selectedOpportunity.id,
          file
        );

      const paymentFromUpload =
        result?.payment || {};

      const mergedPayment = {
        ...(selectedOpportunity.payment || {}),
        ...paymentFromUpload,

        // Le choix local de l'utilisateur est prioritaire :
        // l'upload ne doit jamais réinitialiser le formulaire.
        method:
          paymentDraft.method
          || paymentFromUpload.method
          || selectedOpportunity.payment?.method
          || "",
        provider: paymentDraft.provider || "",
        reference: paymentDraft.reference || "",
        comment: paymentDraft.comment || "",
        quoteId:
          paymentDraft.quoteId
          || paymentFromUpload.quoteId
          || selectedOpportunity.primaryQuoteId
          || "",
      };

      const next = {
        ...selectedOpportunity,
        payment: mergedPayment,
      };

      setPaymentDraft((current) => ({
        ...current,
        ...paymentFromUpload,
        method:
          current.method
          || paymentFromUpload.method
          || "",
        provider: current.provider,
        reference: current.reference,
        comment: current.comment,
        quoteId:
          current.quoteId
          || paymentFromUpload.quoteId
          || selectedOpportunity.primaryQuoteId
          || "",
      }));

      setSelectedOpportunity(next);

      setBoard((current) => ({
        ...current,
        opportunities: current.opportunities.map(
          (item) =>
            item.id === next.id
              ? next
              : item
        ),
      }));

      toast.success(
        "Justificatif ajouté et classé dans Documents / Justificatifs."
      );
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Upload du justificatif impossible."
      );
    } finally {
      setProofUploading(false);
    }
  };

  const runQuoteAction = async ({
    opportunity,
    action,
    successMessage,
    targetColumn = "",
    closeDrawer = false,
  }) => {
    if (!opportunity?.primaryQuoteId || quoteActionSaving) return;
    setQuoteActionSaving(true);
    try {
      await action(opportunity.primaryQuoteId);
      toast.success(successMessage);
      if (targetColumn) setOpenMobileColumn(targetColumn);
      if (closeDrawer) setSelectedOpportunity(null);
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Action impossible sur le devis."
      );
    } finally {
      setQuoteActionSaving(false);
    }
  };

  const openSecureQuoteShare = useCallback((opportunity) => {
    if (!opportunity?.primaryQuoteId) {
      toast.error("Aucun devis validé à envoyer.");
      return;
    }

    setSelectedOpportunity(null);
    setShareOpportunity(opportunity);
  }, []);

  // ACTA_AFFAIR_ARCHIVE_ADMIN_ONLY_V1
  const archiveSelectedOpportunity = async () => {
    if (
      !isAdmin
      || !archiveConfirmOpportunity?.id
      || archiveSaving
    ) {
      return;
    }

    setArchiveSaving(true);

    try {
      await opportunitiesApi.remove(
        archiveConfirmOpportunity.id
      );
      toast.success("Affaire archivée.");
      setArchiveConfirmOpportunity(null);
      setSelectedOpportunity(null);
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Archivage de l’affaire impossible."
      );
    } finally {
      setArchiveSaving(false);
    }
  };

  const rejectSelectedQuote = async () => {
    const reason = quoteRejectReason.trim();
    if (!selectedOpportunity?.primaryQuoteId || !reason) {
      toast.error("Le motif de correction est obligatoire.");
      return;
    }
    await runQuoteAction({
      opportunity: selectedOpportunity,
      action: (quoteId) => quotesApi.reject(quoteId, reason),
      successMessage: "Correction demandée. Le devis revient dans Brouillons.",
      targetColumn: "draft",
      closeDrawer: true,
    });
    setQuoteRejectReason("");
  };

  const decideSelectedFinancing = async (decision) => {
    if (!selectedOpportunity?.primaryQuoteId || financingSaving) return;
    if (decision !== "approved" && !financingDecisionReason.trim()) {
      toast.error("Le motif est obligatoire pour refuser ou annuler le financement.");
      return;
    }
    setFinancingSaving(true);
    try {
      await quotesApi.decideFinancing(
        selectedOpportunity.primaryQuoteId,
        {
          decision,
          reason: financingDecisionReason.trim(),
          provider: financingProviderDraft.trim(),
          prepareGmao: false,
        }
      );
      toast.success(
        decision === "approved"
          ? "Financement validé."
          : decision === "refused"
            ? "Financement refusé."
            : "Financement annulé."
      );
      setFinancingDecisionReason("");
      setSelectedOpportunity(null);
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Décision de financement impossible."
      );
    } finally {
      setFinancingSaving(false);
    }
  };

  const createSelectedPurchaseOrder = async () => {
    if (!selectedOpportunity?.primaryQuoteId || purchaseOrderSaving) return;
    setPurchaseOrderSaving(true);
    try {
      await quotesApi.createPurchaseOrder(
        selectedOpportunity.primaryQuoteId,
        { payment_status: "pending" }
      );
      toast.success("Bon de commande créé.");
      setSelectedOpportunity(null);
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Création du bon de commande impossible."
      );
    } finally {
      setPurchaseOrderSaving(false);
    }
  };

  const signSelectedPurchaseOrder = async () => {
    const orderId = selectedOpportunity?.primaryQuotePurchaseOrderId;
    if (!orderId || purchaseOrderSaving) return;
    setPurchaseOrderSaving(true);
    try {
      await quotesApi.signPurchaseOrder(orderId);
      toast.success("Bon de commande marqué comme signé.");
      setSelectedOpportunity(null);
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Validation du bon de commande impossible."
      );
    } finally {
      setPurchaseOrderSaving(false);
    }
  };

  const finalizeSelectedSale = async () => {
    if (!selectedOpportunity?.primaryQuoteId || finalizingSale) return;
    setFinalizingSale(true);
    try {
      await quotesApi.finalizeSale(
        selectedOpportunity.primaryQuoteId,
        { prepareGmao: true }
      );
      toast.success("Vente finalisée et affaire gagnée.");
      setSelectedOpportunity(null);
      setOpenMobileColumn("won");
      await loadBoard();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "La vente ne peut pas encore être finalisée."
      );
    } finally {
      setFinalizingSale(false);
    }
  };

  const closePipelineDropAction = () => {
    if (movingId) return;
    setPipelineDropAction(null);
  };

  const confirmPipelineDropAction = async () => {
    const action = pipelineDropAction;
    if (!action || movingId || quoteActionSaving) return;

    setPipelineDropAction(null);
    const item = action.opportunity;
    const actionType = action.requirement.actionType;

    if (actionType === "create_quote") {
      setQuoteOpportunity(item);
      return;
    }
    if (actionType === "submit_quote") {
      await runQuoteAction({
        opportunity: item,
        action: quotesApi.submit,
        successMessage: "Devis soumis à validation.",
        targetColumn: "validation",
      });
      return;
    }
    if (actionType === "reject_quote") {
      setQuoteRejectReason("");
      setSelectedOpportunity(item);
      return;
    }
    if (actionType === "approve_quote") {
      await runQuoteAction({
        opportunity: item,
        action: quotesApi.approve,
        successMessage: "Devis validé par l’administrateur.",
        targetColumn: "ready",
      });
      return;
    }
    if (actionType === "send_quote") {
      openSecureQuoteShare(item);
      return;
    }
    if (actionType === "create_revision") {
      setQuoteOpportunity(item);
      return;
    }
    if (actionType === "accept_quote") {
      await runQuoteAction({
        opportunity: item,
        action: quotesApi.accept,
        successMessage: "Acceptation client enregistrée.",
        targetColumn: "finalization",
      });
      return;
    }
    if (["open_affair", "open_finalization"].includes(actionType)) {
      setSelectedOpportunity(item);
    }
  };


  if (loading) {
    return (
      <ActaStandardPage
        testId="pipeline-loading-state"
        className="pipeline-viewport-page acta-responsive-pipeline lg:p-6"
      >
        <section className="pipeline-final-metrics pipeline-loading-grid" aria-label="Chargement des indicateurs">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="pipeline-final-metric pipeline-skeleton-card" />
          ))}
        </section>
        <div className="pipeline-affaires-identity"><strong>Affaires</strong></div>
        <section className="pipeline-loading-columns" aria-label="Chargement des affaires">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="pipeline-final-column pipeline-skeleton-column" />
          ))}
        </section>
      </ActaStandardPage>
    );
  }

  if (loadError) {
    return (
      <ActaStandardPage
        testId="pipeline-error-state"
        className="pipeline-viewport-page acta-responsive-pipeline lg:p-6"
      >
        <section className="pipeline-error-panel" role="alert">
          <XCircle size={24} weight="fill" />
          <strong>Impossible de charger les affaires.</strong>
          <span>{loadError}</span>
          <button type="button" onClick={loadBoard}>Réessayer</button>
        </section>
      </ActaStandardPage>
    );
  }

  return (
    <ActaStandardPage
      testId="commerce-pipeline-page"
      className="pipeline-viewport-page acta-responsive-pipeline lg:p-6"
    >
      {/* ACTA_AFFAIRES_STATS_LAYOUT_V38 */}
      <section
        className="pipeline-final-metrics"
        data-testid="pipeline-opportunity-metrics"
      >
        <CommerceMetricCard
          icon={Briefcase}
          label="Affaires actives"
          value={metrics.active}
          detail="En cours"
          tone={THEME_COLORS.primary}
        />

        <CommerceMetricCard
          icon={TrendUp}
          label="CA prévisionnel"
          value={formatCurrency(metrics.forecast)}
          detail="Sur les affaires actives"
          tone={THEME_COLORS.success}
        />

        <CommerceMetricCard
          icon={CheckCircle}
          label="CA gagné"
          value={formatCurrency(metrics.won)}
          detail="Sur les ventes finalisées"
          tone={THEME_COLORS.orange}
        />
      </section>

      <div
        className="pipeline-affaires-identity md:hidden"
        data-testid="affaires-page-identity"
      >
        <strong>Affaires</strong>
      </div>

      <section
        className="pipeline-mobile-filter-summary"
        data-testid="pipeline-mobile-filter-summary"
      >
        <div className="pipeline-mobile-filter-item">
          <button
            type="button"
            onClick={() => {
              setMobileFilterSubMenu("");
              setMobileFilterSheet((current) =>
                current === "period" ? "" : "period"
              );
            }}
            aria-expanded={mobileFilterSheet === "period"}
          >
            <CalendarBlank size={18} weight="bold" />

            <span>
              <strong>Période</strong>
              <small>{selectedPeriodLabel}</small>
            </span>

            <CaretRight
              size={18}
              className={
                mobileFilterSheet === "period"
                  ? "pipeline-mobile-filter-caret is-open"
                  : "pipeline-mobile-filter-caret"
              }
            />
          </button>

          {mobileFilterSheet === "period" && (
            <div
              className="pipeline-inline-dropdown pipeline-inline-period"
              role="menu"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const today = toDateKey(new Date());

                  setPipelinePeriodMode("between");
                  setPipelinePeriodDateFrom(today);
                  setPipelinePeriodDateTo(today);
                  setMobileFilterSubMenu("");
                  setMobileFilterSheet("");
                }}
              >
                Aujourd’hui
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const current = new Date();
                  const day = current.getDay();
                  const mondayOffset = day === 0 ? -6 : 1 - day;

                  const monday = new Date(current);
                  monday.setDate(current.getDate() + mondayOffset);

                  const sunday = new Date(monday);
                  sunday.setDate(monday.getDate() + 6);

                  setPipelinePeriodMode("between");
                  setPipelinePeriodDateFrom(toDateKey(monday));
                  setPipelinePeriodDateTo(toDateKey(sunday));
                  setMobileFilterSubMenu("");
                  setMobileFilterSheet("");
                }}
              >
                Cette semaine
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const current = new Date();

                  const firstDay = new Date(
                    current.getFullYear(),
                    current.getMonth(),
                    1
                  );

                  const lastDay = new Date(
                    current.getFullYear(),
                    current.getMonth() + 1,
                    0
                  );

                  setPipelinePeriodMode("between");
                  setPipelinePeriodDateFrom(toDateKey(firstDay));
                  setPipelinePeriodDateTo(toDateKey(lastDay));
                  setMobileFilterSubMenu("");
                  setMobileFilterSheet("");
                }}
              >
                Ce mois
              </button>

              <button
                type="button"
                role="menuitem"
                className={
                  mobileFilterSubMenu === "custom-period"
                    ? "is-selected"
                    : ""
                }
                onClick={() => {
                  setPipelinePeriodMode("between");
                  setMobileFilterSubMenu("custom-period");
                }}
              >
                Personnalisé
                <CaretRight size={15} />
              </button>

              {mobileFilterSubMenu === "custom-period" && (
                <div className="pipeline-inline-custom-period">
                  <label>
                    <span>Du</span>
                    <input
                      type="date"
                      value={pipelinePeriodDateFrom}
                      onChange={(event) =>
                        setPipelinePeriodDateFrom(event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span>Au</span>
                    <input
                      type="date"
                      value={pipelinePeriodDateTo}
                      onChange={(event) =>
                        setPipelinePeriodDateTo(event.target.value)
                      }
                    />
                  </label>

                  <button
                    type="button"
                    className="pipeline-inline-apply"
                    disabled={
                      !pipelinePeriodDateFrom
                      || !pipelinePeriodDateTo
                      || pipelinePeriodDateFrom > pipelinePeriodDateTo
                    }
                    onClick={() => {
                      setMobileFilterSubMenu("");
                      setMobileFilterSheet("");
                    }}
                  >
                    Appliquer
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {canFilterCommercials && (
          <div className="pipeline-mobile-filter-item">
            <button
              type="button"
              onClick={() => {
                setMobileFilterSubMenu("");
                setMobileFilterSheet((current) =>
                  current === "account" ? "" : "account"
                );
              }}
              aria-expanded={mobileFilterSheet === "account"}
            >
              <UserCircle size={18} weight="bold" />

              <span>
                <strong>Commercial</strong>
                <small>{selectedCommercialLabel}</small>
              </span>

              <CaretRight
                size={18}
                className={
                  mobileFilterSheet === "account"
                    ? "pipeline-mobile-filter-caret is-open"
                    : "pipeline-mobile-filter-caret"
                }
              />
            </button>

            {mobileFilterSheet === "account" && (
              <div
                className="pipeline-inline-dropdown pipeline-inline-account"
                role="menu"
              >
                {!mobileFilterSubMenu && (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className={
                        pipelineFilterValue === "all"
                          ? "is-selected"
                          : ""
                      }
                      onClick={() => {
                        setPipelineFilterValue("all");
                        setMobileFilterSheet("");
                      }}
                    >
                      Tous les commerciaux
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      className={
                        pipelineFilterValue === "self"
                          ? "is-selected"
                          : ""
                      }
                      onClick={() => {
                        setPipelineFilterValue("self");
                        setMobileFilterSheet("");
                      }}
                    >
                      Moi — utilisateur en cours
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        setMobileFilterSubMenu("roles")
                      }
                    >
                      Par rôle
                      <CaretRight size={15} />
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        setMobileFilterSubMenu("users")
                      }
                    >
                      Par utilisateur
                      <CaretRight size={15} />
                    </button>
                  </>
                )}

                {mobileFilterSubMenu === "roles" && (
                  <>
                    <button
                      type="button"
                      className="pipeline-inline-back"
                      onClick={() => setMobileFilterSubMenu("")}
                    >
                      ← Retour
                    </button>

                    {[
                      ["role:admin", "Administrateurs"],
                      ["role:manager", "Managers"],
                      ["role:user", "Commerciaux uniquement"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        role="menuitem"
                        className={
                          pipelineFilterValue === value
                            ? "is-selected"
                            : ""
                        }
                        onClick={() => {
                          setPipelineFilterValue(value);
                          setMobileFilterSubMenu("");
                          setMobileFilterSheet("");
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </>
                )}

                {mobileFilterSubMenu === "users" && (
                  <>
                    <button
                      type="button"
                      className="pipeline-inline-back"
                      onClick={() => setMobileFilterSubMenu("")}
                    >
                      ← Retour
                    </button>

                    <div className="pipeline-inline-user-list">
                      {pipelineCommercialUsers.map((entry) => (
                        <button
                          key={entry.id}
                          type="button"
                          role="menuitem"
                          className={
                            pipelineFilterValue
                              === `owner:${entry.id}`
                              ? "is-selected"
                              : ""
                          }
                          onClick={() => {
                            setPipelineFilterValue(
                              `owner:${entry.id}`
                            );
                            setMobileFilterSubMenu("");
                            setMobileFilterSheet("");
                          }}
                        >
                          <span>{entry.name}</span>
                          <small>
                            {entry.role === "admin"
                              ? "Administrateur"
                              : entry.role === "manager"
                                ? "Manager"
                                : "Commercial"}
                          </small>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section
        className="pipeline-final-toolbar lirah-card p-3"
        data-testid="pipeline-final-toolbar"
        aria-busy={refreshing}
        data-refreshing={refreshing ? "true" : "false"}
      >
        <label className="pipeline-final-search">
          <MagnifyingGlass size={17} weight="bold" />
          <span className="sr-only">
            Rechercher dans les affaires
          </span>
          <input
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="Rechercher une affaire…"
            data-testid="commerce-pipeline-search"
          />
        </label>

        <div
          className="pipeline-desktop-period-presets"
          data-testid="pipeline-desktop-period-presets-v39"
        >
          {PIPELINE_DESKTOP_PERIOD_PRESETS.map(
            ([preset, label]) => (
              <button
                key={preset}
                type="button"
                className={
                  pipelineDesktopPeriodPreset === preset
                    ? "is-active"
                    : ""
                }
                onClick={() =>
                  applyDesktopPeriodPreset(preset)
                }
              >
                {label}
              </button>
            )
          )}

          <div className="pipeline-desktop-custom-period">
            <button
              type="button"
              className={
                pipelineDesktopPeriodPreset === "custom"
                  ? "pipeline-desktop-custom-trigger is-active"
                  : "pipeline-desktop-custom-trigger"
              }
              aria-expanded={pipelineDesktopCustomOpen}
              onClick={() => {
                const today = toDateKey(new Date());

                setPipelineDesktopPeriodPreset("custom");
                setPipelinePeriodMode("between");
                setPipelinePeriodDateFrom(
                  (value) => value || today
                );
                setPipelinePeriodDateTo(
                  (value) => value || today
                );
                setPipelineDesktopCustomOpen(
                  (current) => !current
                );
              }}
            >
              <CalendarBlank size={14} weight="bold" />
              Personnalisé
            </button>

            {pipelineDesktopCustomOpen && (
              <section className="pipeline-desktop-custom-popover">
                <div className="pipeline-desktop-custom-fields">
                  <label>
                    <span>Date de début</span>
                    <input
                      type="date"
                      value={pipelinePeriodDateFrom}
                      onChange={(event) =>
                        setPipelinePeriodDateFrom(
                          event.target.value
                        )
                      }
                    />
                  </label>

                  <label>
                    <span>Date de fin</span>
                    <input
                      type="date"
                      value={pipelinePeriodDateTo}
                      onChange={(event) =>
                        setPipelinePeriodDateTo(
                          event.target.value
                        )
                      }
                    />
                  </label>
                </div>

                <button
                  type="button"
                  className="pipeline-desktop-custom-apply"
                  disabled={
                    !pipelinePeriodDateFrom
                    || !pipelinePeriodDateTo
                    || pipelinePeriodDateFrom
                      > pipelinePeriodDateTo
                  }
                  onClick={() =>
                    setPipelineDesktopCustomOpen(false)
                  }
                >
                  Appliquer
                </button>
              </section>
            )}
          </div>
        </div>

        <div
          className={`pipeline-final-period pipeline-final-period-mobile is-${pipelinePeriodMode}`}
          data-testid="pipeline-period-filter"
        >
          <CalendarBlank size={17} weight="bold" />
          <select
            value={pipelinePeriodMode}
            onChange={(event) =>
              applyPipelinePeriodMode(event.target.value)
            }
            data-testid="pipeline-period-mode"
            aria-label="Mode de période"
          >
            {PIPELINE_PERIOD_MODES.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>

          {pipelinePeriodMode !== "before" && (
            <input
              type="date"
              value={pipelinePeriodDateFrom}
              onChange={(event) =>
                setPipelinePeriodDateFrom(
                  event.target.value
                )
              }
              data-testid="pipeline-period-date-from"
              aria-label="Date de début"
            />
          )}

          {pipelinePeriodMode !== "after" && (
            <input
              type="date"
              value={pipelinePeriodDateTo}
              onChange={(event) =>
                setPipelinePeriodDateTo(
                  event.target.value
                )
              }
              data-testid="pipeline-period-date-to"
              aria-label="Date de fin"
            />
          )}
        </div>

        {canFilterCommercials && (
          <div
            className="min-w-0 md:w-[220px]"
            data-testid="pipeline-admin-owner-filter"
          >
            <CommercialScopeFilter
              value={pipelineFilterValue}
              onChange={setPipelineFilterValue}
              options={pipelineCommercialOptions}
              ariaLabel="Filtrer les affaires par commercial"
              testId="pipeline-owner-user-filter"
            />
          </div>
        )}




        <Button
          type="button"
          variant="actaPrimary"
          onClick={openOpportunityModal}
          className="pipeline-final-create pipeline-final-create-toolbar"
          data-testid="pipeline-new-opportunity"
          aria-label="Nouvelle affaire"
        >
          <Plus size={17} weight="bold" />
          <span className="pipeline-final-create-label">Nouvelle affaire</span>
        </Button>
      </section>

      {refreshing && (
        <div className="pipeline-filter-refresh" role="status" aria-live="polite">
          <span /> Mise à jour des affaires…
        </div>
      )}

      <section
        className="pipeline-final-columns pipeline-columns-grid acta-responsive-pipeline acta-commerce-page-standard mt-4 grid w-full max-w-full min-w-0"
        data-testid="commerce-pipeline-columns"
        data-visible-columns={visibleColumns.length}
        style={{
          "--pipeline-visible-columns":
            visibleColumns.length,
        }}
      >
          {visibleColumns.map((column) => {
            const items = grouped[column.key] || [];
            const amount = items.reduce(
              (sum, item) => sum + amountForTotal(item.estimatedAmount),
              0
            );
            const totalPages = Math.max(
              1,
              Math.ceil(items.length / pipelinePageSize)
            );
            const requestedPage = columnPages[column.key] || 1;
            const currentPage = Math.min(requestedPage, totalPages);
            const visibleItems = items.slice(
              (currentPage - 1) * pipelinePageSize,
              currentPage * pipelinePageSize
            );


            return (
              <section
                key={column.key}
                className={`pipeline-column pipeline-final-column min-w-0 ${
                  openMobileColumn === column.key
                    ? "is-mobile-open"
                    : "is-mobile-closed max-md:min-h-0"
                } ""`}
                data-stage={column.key}
              >
                <header className="pipeline-column-header pipeline-final-column-header">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenMobileColumn((current) =>
                        current === column.key ? "" : column.key
                      )
                    }
                    className="flex w-full items-center justify-between gap-3 text-left md:cursor-default"
                    aria-expanded={openMobileColumn === column.key}
                    data-testid={`pipeline-mobile-toggle-${column.key}`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: column.color }}
                      />
                      <h2 className="truncate font-heading text-xs font-black text-[var(--do-ink)]">
                        {column.label}
                      </h2>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="pipeline-column-count-pill rounded-full bg-[var(--do-surface-soft)] px-2 py-0.5 text-xs font-heading font-bold text-[var(--do-muted)]">
                        {items.length}
                      </span>
                      <CaretDown
                        size={15}
                        weight="bold"
                        className={`transition-transform md:hidden ${
                          openMobileColumn === column.key ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>
                  <div
                    className={`pipeline-column-meta-row mt-2 items-center justify-between gap-2 ${
                      openMobileColumn === column.key ? "flex" : "hidden md:flex"
                    }`}
                  >
                    <span className="pipeline-column-description truncate text-xs text-[var(--do-muted)]">
                      {column.description}
                    </span>

                    <strong className="pipeline-column-amount whitespace-nowrap text-xs text-[var(--do-ink)]">
                      {formatCurrency(amount)}
                    </strong>

                    <span className="pipeline-column-desktop-summary">
                      {items.length}{" "}
                      {items.length === 1 ? "affaire" : "affaires"}
                      {" · "}
                      {formatCurrency(amount)}
                    </span>
                  </div>
                </header>

                <div
                  className={`pipeline-column-body pipeline-final-column-body ${
                    openMobileColumn === column.key ? "block" : "hidden md:block"
                  }`}
                  data-testid={`pipeline-column-scroll-${column.key}`}
                >
                  {!items.length ? (
                    <div className="pipeline-premium-empty-state">
                      <span>
                        <Briefcase
                          size={28}
                          weight="duotone"
                        />
                      </span>
                      <strong>
                        Aucune affaire dans cette colonne.
                      </strong>
                      <small>
                        Les affaires en cours apparaîtront ici.
                      </small>
                    </div>
                  ) : (
                    <>
                      {items.length > pipelinePageSize && (
                        <div className="pipeline-page-summary rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs font-bold text-[var(--do-accent)] md:hidden">
                          Page {currentPage}/{totalPages} · {items.length} affaires
                        </div>
                      )}
                      {visibleItems.map((item) => (
                        <OpportunityCard
                          key={item.id}
                          opportunity={item}
                          moving={movingId === item.id}
                          onOpen={setSelectedOpportunity}
                          onSend={openSecureQuoteShare}
                          onPreview={previewOpportunityQuote}
                        />
                      ))}
                    </>
                  )}
                </div>

                {items.length > pipelinePageSize && (
                  <footer
                    className={`pipeline-column-pagination items-center justify-between gap-2 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-2.5 ${
                      openMobileColumn === column.key ? "flex" : "hidden md:flex"
                    }`}
                    data-testid={`pipeline-pagination-${column.key}`}
                  >
                    <button
                      type="button"
                      onClick={() => setColumnPage(column.key, currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="rounded-lg border border-[var(--do-border)] px-2.5 py-1 text-xs font-black text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Page précédente ${column.label}`}
                    >
                      <CaretLeft size={15} weight="bold" />
                    </button>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setColumnPage(column.key, currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="rounded-lg border border-[var(--do-border)] px-2.5 py-1 text-xs font-black text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Page suivante ${column.label}`}
                    >
                      <CaretRight size={15} weight="bold" />
                    </button>
                  </footer>
                )}
              </section>
            );
          })}
      </section>

      {archiveConfirmOpportunity
        && isAdmin
        && typeof document !== "undefined"
        ? createPortal(
            <div
              className="acta-modal-overlay fixed inset-0 z-[150] flex items-center justify-center p-3 sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget
              && !archiveSaving
            ) {
              setArchiveConfirmOpportunity(null);
            }
          }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="affair-archive-title"
            aria-describedby="affair-archive-description"
            className="w-full max-w-lg rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 shadow-2xl sm:p-6"
            data-testid="affair-archive-confirm"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[rgba(244,63,94,0.10)] text-[var(--do-rose)]">
                <Trash size={22} weight="bold" />
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id="affair-archive-title"
                  className="font-heading text-xl font-black text-[var(--do-ink)]"
                >
                  Archiver cette affaire ?
                </h2>
                <p
                  id="affair-archive-description"
                  className="mt-2 text-sm leading-6 text-[var(--do-muted)]"
                >
                  Elle disparaîtra du Pipeline et des vues normales.
                  Elle restera récupérable depuis le terminal par un
                  administrateur système.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setArchiveConfirmOpportunity(null)}
                disabled={archiveSaving}
                className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={archiveSelectedOpportunity}
                disabled={archiveSaving}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[var(--do-rose)] bg-[var(--do-rose)] px-4 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="affair-archive-confirm-button"
              >
                <Trash size={18} weight="bold" />
                {archiveSaving ? "Archivage…" : "Archiver l’affaire"}
              </button>
            </div>
          </section>
            </div>,
            document.body
          )
        : null}

      {selectedOpportunityNeedsQuoteValidation && (
        <div
          className="acta-modal-overlay fixed inset-0 z-[125] flex items-center justify-center p-3 sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget
              && !quoteActionSaving
            ) {
              setSelectedOpportunity(null);
            }
          }}
        >
          <section
            className="acta-modal-surface flex h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl sm:h-[min(88dvh,860px)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-validation-title"
            data-testid="quote-validation-modal"
          >
            <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--do-border)] px-4 py-4 sm:px-6">
              <div className="min-w-0">
                <div className="label-eyebrow">Validation devis</div>
                <h2
                  id="quote-validation-title"
                  className="mt-1 truncate font-heading text-xl font-black text-[var(--do-ink)] sm:text-2xl"
                >
                  {selectedOpportunity.primaryQuoteNumber || "Devis à valider"}
                  {selectedOpportunity.primaryQuoteVersion
                    ? ` · V${selectedOpportunity.primaryQuoteVersion}`
                    : ""}
                </h2>
                <p className="mt-1 truncate text-sm text-[var(--do-muted)]">
                  {selectedOpportunity.prospectName
                    || selectedOpportunity.prospectCompany
                    || selectedOpportunity.title}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setArchiveConfirmOpportunity(selectedOpportunity)}
                  disabled={quoteActionSaving || archiveSaving}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border border-[var(--do-rose)] bg-transparent px-3 text-sm font-black text-[var(--do-rose)] transition hover:bg-[rgba(244,63,94,0.08)] disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="affair-archive-validation-button"
                >
                  <Trash size={18} weight="bold" />
                  <span className="hidden sm:inline">Supprimer l’affaire</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedOpportunity(null)}
                  disabled={quoteActionSaving}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] disabled:opacity-50"
                  aria-label="Fermer la validation du devis"
                  data-testid="quote-validation-close"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>
            </header>

            <div className="grid min-h-0 flex-1 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_320px]">
              <div className="min-h-0 bg-[var(--do-surface-soft)] p-3 sm:p-4">
                <div className="flex h-full min-h-[320px] items-center justify-center overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
                  {quoteValidationPdfLoading ? (
                    <div
                      className="px-6 text-center text-sm font-bold text-[var(--do-muted)]"
                      role="status"
                      data-testid="quote-validation-pdf-loading"
                    >
                      Chargement du devis…
                    </div>
                  ) : quoteValidationPdfError ? (
                    <div
                      className="max-w-md px-6 text-center"
                      role="alert"
                      data-testid="quote-validation-pdf-error"
                    >
                      <div className="font-black text-[var(--do-ink)]">
                        Prévisualisation indisponible
                      </div>
                      <p className="mt-2 text-sm leading-6 text-[var(--do-muted)]">
                        {quoteValidationPdfError}
                      </p>
                    </div>
                  ) : quoteValidationPdfUrl ? (
                    <iframe
                      src={quoteValidationPdfUrl}
                      title={`Prévisualisation ${selectedOpportunity.primaryQuoteNumber || "du devis"}`}
                      className="h-full min-h-[320px] w-full border-0"
                      data-testid="quote-validation-pdf"
                    />
                  ) : null}
                </div>
              </div>

              <aside className="min-h-0 overflow-y-auto border-t border-[var(--do-border)] p-4 sm:border-l sm:border-t-0 sm:p-5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-[var(--do-surface-soft)] p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--do-muted)]">
                      Montant
                    </div>
                    <div className="mt-1 text-base font-black text-[var(--do-ink)]">
                      {formatCurrency(selectedOpportunity.estimatedAmount)}
                    </div>
                  </div>

                  <div className="rounded-xl bg-[var(--do-surface-soft)] p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--do-muted)]">
                      Statut
                    </div>
                    <div className="mt-1 text-base font-black text-[var(--do-ink)]">
                      À valider
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-[var(--do-border)] p-4">
                  <h3 className="font-heading text-base font-black text-[var(--do-ink)]">
                    Décision administrative
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-[var(--do-muted)]">
                    Contrôlez le PDF puis validez le devis ou demandez une correction.
                  </p>

                  {quoteValidationPdfUrl && (
                    <a
                      href={quoteValidationPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex w-full items-center justify-center rounded-xl border border-[var(--do-border)] px-3 py-2.5 text-sm font-black text-[var(--do-accent)]"
                    >
                      Ouvrir le PDF
                    </a>
                  )}

                  <button
                    type="button"
                    disabled={quoteActionSaving}
                    onClick={() =>
                      runQuoteAction({
                        opportunity: selectedOpportunity,
                        action: quotesApi.approve,
                        successMessage: "Devis validé.",
                        targetColumn: "ready",
                        closeDrawer: true,
                      })
                    }
                    className="mt-3 min-h-[46px] w-full rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="quote-validation-approve"
                  >
                    {quoteActionSaving ? "Enregistrement…" : "Valider le devis"}
                  </button>
                </div>

                <div className="mt-4 rounded-2xl border border-[var(--do-border)] p-4">
                  <label
                    htmlFor="quote-validation-reason"
                    className="text-sm font-black text-[var(--do-ink)]"
                  >
                    Demander une correction
                  </label>
                  <textarea
                    id="quote-validation-reason"
                    rows={3}
                    maxLength={1000}
                    value={quoteRejectReason}
                    onChange={(event) => setQuoteRejectReason(event.target.value)}
                    placeholder="Motif obligatoire"
                    className="mt-2 w-full resize-none rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="quote-validation-reason"
                  />
                  <button
                    type="button"
                    disabled={quoteActionSaving || !quoteRejectReason.trim()}
                    onClick={rejectSelectedQuote}
                    className="mt-2 min-h-[44px] w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="quote-validation-reject"
                  >
                    Demander la correction
                  </button>
                </div>

              </aside>
            </div>
          </section>
        </div>
      )}

      {selectedOpportunity && !selectedOpportunityNeedsQuoteValidation && (
        <div
          className="acta-modal-overlay fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedOpportunity(null);
            }
          }}
        >
          <aside
            className="acta-modal-surface affair-detail-modal flex w-full max-w-xl flex-col overflow-hidden rounded-3xl sm:h-auto sm:max-h-[calc(100dvh-2rem)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="affair-detail-title"
            data-testid="affair-detail-drawer"
          >
            <div className="affair-detail-header flex items-start justify-between gap-4 p-4 sm:p-6 sm:pb-4">
              <div>
                <div className="label-eyebrow">Affaire</div>
                <h2 id="affair-detail-title" className="mt-1 font-heading text-2xl font-black">{selectedOpportunity.title}</h2>
                <p className="mt-1 text-sm text-[var(--do-muted)]">{selectedOpportunity.prospectName || selectedOpportunity.prospectCompany}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setArchiveConfirmOpportunity(selectedOpportunity)}
                    disabled={archiveSaving}
                    className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border border-[var(--do-rose)] bg-transparent px-3 text-sm font-black text-[var(--do-rose)] transition hover:bg-[rgba(244,63,94,0.08)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="affair-archive-button"
                  >
                    <Trash size={18} weight="bold" />
                    <span className="hidden sm:inline">Supprimer l’affaire</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedOpportunity(null)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] transition hover:bg-[var(--do-bg-solid)]"
                  aria-label="Fermer"
                  data-testid="affair-detail-close"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>
            </div>

            <div className="affair-detail-summary grid grid-cols-3 gap-2 px-4 pb-3 sm:gap-3 sm:px-6 sm:pb-4">
              <div className="rounded-xl bg-[var(--do-bg-solid)] p-2.5 sm:rounded-2xl sm:p-4"><div className="text-[10px] font-bold text-[var(--do-muted)] sm:text-xs">Montant</div><div className="mt-1 text-base font-black sm:text-xl">{formatCurrency(selectedOpportunity.estimatedAmount)}</div></div>
              <div className="rounded-xl bg-[var(--do-bg-solid)] p-2.5 sm:rounded-2xl sm:p-4"><div className="text-[10px] font-bold text-[var(--do-muted)] sm:text-xs">Étape</div><div className="mt-1 text-sm font-black sm:text-base">{opportunityStageBadge(selectedOpportunity).label}</div></div>
              <div className="rounded-xl bg-[var(--do-bg-solid)] p-2.5 sm:rounded-2xl sm:p-4"><div className="text-[10px] font-bold text-[var(--do-muted)] sm:text-xs">Devis</div><div className="mt-1 truncate text-sm font-black sm:text-base">{selectedOpportunity.primaryQuoteNumber || "À créer"}{selectedOpportunity.primaryQuoteVersion ? ` · V${selectedOpportunity.primaryQuoteVersion}` : ""}</div></div>
            </div>

            <div className="affair-detail-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-3 sm:px-6 sm:pb-4">
              {opportunityWorkflowColumn(selectedOpportunity) === "client" && (
                <section className="rounded-2xl border border-[var(--do-border)] p-4" data-testid="affair-step-client">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="label-eyebrow">Attente client</div>
                      <h3 className="mt-1 font-heading text-lg font-black">Réponse du client</h3>
                      <p className="mt-1 text-sm text-[var(--do-muted)]">Le devis a été envoyé. Enregistrez uniquement la réponse réelle du client.</p>
                    </div>
                    <button type="button" onClick={previewSelectedQuote} disabled={!selectedOpportunity.primaryQuoteId} className="rounded-xl border border-[var(--do-border)] px-3 py-2 text-xs font-black text-[var(--do-accent)] disabled:opacity-50" data-testid="affair-client-preview-quote">Voir le devis</button>
                  </div>
                  <div className="mt-4 rounded-xl bg-[var(--do-surface-soft)] p-3 text-sm"><strong>Destinataire : </strong>{selectedOpportunity.prospectEmail || selectedOpportunity.prospectName || selectedOpportunity.prospectCompany || "Client"}</div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-3">
                    <button type="button" disabled={quoteActionSaving} onClick={() => runQuoteAction({ opportunity: selectedOpportunity, action: quotesApi.accept, successMessage: "Acceptation client enregistrée.", targetColumn: "finalization", closeDrawer: true })} className="min-h-[46px] rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50" data-testid="affair-client-accept">Accepte</button>
                    <button type="button" disabled={quoteActionSaving} onClick={() => { setLostOpportunity(selectedOpportunity); setLostOpportunityReason(""); setSelectedOpportunity(null); }} className="min-h-[46px] rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-accent)] disabled:opacity-50" data-testid="affair-client-refuse">Refuse</button>
                    <button type="button" disabled={quoteActionSaving} onClick={() => { setQuoteOpportunity(selectedOpportunity); setSelectedOpportunity(null); }} className="min-h-[46px] rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50" data-testid="affair-client-negotiate">Négocie / nouveau devis</button>
                  </div>
                </section>
              )}

              {opportunityWorkflowColumn(selectedOpportunity) === "finalization" && (
                <section className="rounded-2xl border border-[var(--do-border)] p-4" data-testid="affair-finalization-simplified">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="label-eyebrow">Finalisation</div>
                      <h3 className="mt-1 font-heading text-lg font-black">Finalisation</h3>
                      <p className="mt-1 text-sm text-[var(--do-muted)]">Le devis est accepté. Sélectionnez le mode de paiement avant validation finale.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={previewSelectedQuote} disabled={!selectedOpportunity.primaryQuoteId} className="rounded-xl border border-[var(--do-border)] px-3 py-2 text-xs font-black text-[var(--do-accent)] disabled:opacity-50">Voir le devis</button>
                      {isAdmin
                        && opportunityQuoteStatus(selectedOpportunity) === "accepted" && (
                          <button
                            type="button"
                            onClick={exportSelectedQuoteToEvoliz}
                            disabled={evolizExporting}
                            className="rounded-xl bg-[var(--do-ink)] px-3 py-2 text-xs font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid="affair-evoliz-export"
                          >
                            {evolizExporting
                              ? "Export…"
                              : "Exporter vers Evoliz"}
                          </button>
                        )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="text-xs font-bold">Mode de paiement<select value={paymentDraft.method} onChange={(event) => setPaymentDraft((current) => ({ ...current, method: event.target.value }))} disabled={selectedOpportunity.payment?.status === "validated"} className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] px-3 text-sm" data-testid="affair-finalization-payment-method"><option value="">Choisir</option><option value="cash">Paiement comptant</option><option value="bank_transfer">Virement</option><option value="card">Carte</option><option value="cheque">Chèque</option><option value="bank_financing">Financement</option><option value="leasing">Leasing / location</option><option value="third_party">Tiers financeur</option><option value="other">Autre</option></select></label>
                  </div>
                  <div className="mt-3 rounded-xl border border-[var(--do-border)] p-3">
                    <div className="text-sm font-black">Justificatif{" "}<span className="font-bold text-[var(--do-muted)]">{PAYMENT_PROOF_REQUIRED_METHODS.has(paymentDraft.method) ? "(obligatoire)" : "(facultatif)"}</span></div>
                    {selectedOpportunity.payment?.proofDocumentId ? (
                      <div className="mt-2 flex flex-wrap gap-2"><a href={opportunitiesApi.paymentProofFileUrl(selectedOpportunity.id)} target="_blank" rel="noreferrer" className="rounded-xl border border-[var(--do-border)] px-3 py-2 text-xs font-black text-[var(--do-accent)]">Voir le justificatif</a>{selectedOpportunity.payment?.status !== "validated" && (<label className="cursor-pointer rounded-xl border border-[var(--do-border)] px-3 py-2 text-xs font-black">Remplacer<input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" disabled={proofUploading} onChange={(event) => uploadPaymentProof(event.target.files?.[0])} /></label>)}</div>
                    ) : (
                      <label className="mt-2 inline-flex cursor-pointer rounded-xl bg-[var(--do-ink)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)]">{proofUploading ? "Envoi…" : "Ajouter un justificatif"}<input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" disabled={proofUploading} onChange={(event) => uploadPaymentProof(event.target.files?.[0])} /></label>
                    )}
                  </div>
                  <label className="mt-3 block text-xs font-bold">Informations complémentaires<textarea value={paymentDraft.comment || ""} onChange={(event) => setPaymentDraft((current) => ({ ...current, comment: event.target.value }))} disabled={selectedOpportunity.payment?.status === "validated"} rows="3" maxLength={2000} placeholder="Précision utile pour l’administrateur…" className="mt-1 w-full rounded-xl border border-[var(--do-border)] px-3 py-3 text-sm" /></label>
                  <div className="mt-3 rounded-xl bg-[var(--do-accent-soft)] p-3 text-sm" data-testid="affair-finalization-status"><strong>Statut : </strong>{selectedOpportunity.payment?.status === "validated" ? "Validé" : selectedOpportunity.payment?.status === "rejected" ? "Refusé — correction nécessaire" : selectedOpportunity.payment?.status === "pending_review" ? "Validation administrateur en attente" : "Dossier à préparer"}</div>
                  {selectedOpportunity.payment?.status === "rejected" && (<div className="mt-2 rounded-xl border border-[var(--do-border)] p-3 text-sm"><strong>Motif du refus : </strong>{selectedOpportunity.payment?.rejectionReason || "Non précisé"}</div>)}
                  {!isAdmin && selectedOpportunity.payment?.status !== "validated" && (<button type="button" onClick={submitFinalization} disabled={finalizationSubmitting || !paymentDraft.method} className="mt-4 w-full rounded-xl bg-[var(--do-accent)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50" data-testid="affair-finalization-submit">{finalizationSubmitting ? "Envoi…" : "Envoyer à l’administrateur pour validation"}</button>)}
                  {isAdmin
                    && selectedOpportunity.payment?.status !== "validated"
                    && selectedOpportunity.payment?.status !== "pending_review" && (
                      <button
                        type="button"
                        disabled={
                          finalizationSubmitting
                          || !paymentDraft.method
                          || (
                            PAYMENT_PROOF_REQUIRED_METHODS.has(paymentDraft.method)
                            && !selectedOpportunity.payment?.proofDocumentId
                          )
                        }
                        onClick={approveFinalization}
                        className="mt-4 w-full rounded-xl bg-[var(--do-accent)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
                        data-testid="affair-finalization-admin-direct-approve"
                      >
                        {finalizationSubmitting
                          ? "Validation…"
                          : "Valider et gagner"}
                      </button>
                    )}

                  {isAdmin && selectedOpportunity.payment?.status === "pending_review" && (
                    <div className="mt-4 space-y-3">
                      <div className="grid gap-2 sm:grid-cols-2"><select value={paymentDraft.rejectionReasonCode || ""} onChange={(event) => setPaymentDraft((current) => ({ ...current, rejectionReasonCode: event.target.value }))} className="rounded-xl border border-[var(--do-border)] px-3 py-3 text-sm"><option value="">Motif du refus</option><option value="incomplete_file">Dossier incomplet</option><option value="missing_proof">Justificatif manquant</option><option value="insufficient_solvency">Solvabilité insuffisante</option><option value="amount_not_granted">Montant non accordé</option><option value="customer_withdrawal">Client abandonne</option><option value="no_solution_found">Aucune solution trouvée</option><option value="other">Autre</option></select><input value={paymentDraft.rejectionReason || ""} onChange={(event) => setPaymentDraft((current) => ({ ...current, rejectionReason: event.target.value }))} placeholder="Commentaire" className="rounded-xl border border-[var(--do-border)] px-3 py-3 text-sm" /></div>
                      <div className="grid grid-cols-2 gap-2"><button type="button" disabled={finalizationSubmitting || !paymentDraft.rejectionReasonCode} onClick={() => reviewPayment("reject")} className="rounded-xl border border-[var(--do-border)] px-4 py-3 text-sm font-black disabled:opacity-50">Refuser</button><button type="button" disabled={finalizationSubmitting || !paymentDraft.method || (PAYMENT_PROOF_REQUIRED_METHODS.has(paymentDraft.method) && !selectedOpportunity.payment?.proofDocumentId)} onClick={approveFinalization} className="rounded-xl bg-[var(--do-accent)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50" data-testid="affair-finalization-approve">{finalizationSubmitting ? "Validation…" : "Valider et gagner"}</button></div>
                    </div>
                  )}
                </section>
              )}

              {opportunityWorkflowColumn(selectedOpportunity) === "validation" && (
                <section
                  className="rounded-2xl border border-[var(--do-border)] p-4"
                  data-testid="affair-step-quote-waiting-admin"
                >
                  <div className="label-eyebrow">Devis en attente</div>
                  <h3 className="mt-1 font-heading text-lg font-black">
                    Décision administrateur en attente
                  </h3>
                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    Le devis a bien été transmis. Il passera automatiquement
                    dans « À envoyer » après validation.
                  </p>
                </section>
              )}

              {opportunityWorkflowColumn(selectedOpportunity) === "won" && (
                <section className="rounded-2xl border border-[var(--do-border)] p-4" data-testid="affair-step-won">
                  <div className="label-eyebrow">Affaire gagnée</div><div className="mt-2 flex items-start gap-3"><CheckCircle size={28} weight="fill" className="shrink-0 text-[var(--do-accent)]" /><div><h3 className="font-heading text-lg font-black">Vente finalisée</h3><p className="mt-1 text-sm text-[var(--do-muted)]">Cette affaire est clôturée en lecture seule.</p></div></div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2"><div className="rounded-xl bg-[var(--do-surface-soft)] p-3 text-sm"><strong>Montant : </strong>{formatCurrency(selectedOpportunity.estimatedAmount)}</div><div className="rounded-xl bg-[var(--do-surface-soft)] p-3 text-sm"><strong>Paiement : </strong>{selectedOpportunity.payment?.method || "Validé"}</div></div>
                  <button type="button" onClick={previewSelectedQuote} disabled={!selectedOpportunity.primaryQuoteId} className="mt-4 w-full rounded-xl border border-[var(--do-border)] px-4 py-3 text-sm font-black text-[var(--do-accent)] disabled:opacity-50">Voir le devis</button>
                </section>
              )}

              {opportunityWorkflowColumn(selectedOpportunity) === "lost" && (
                <section className="rounded-2xl border border-[var(--do-border)] p-4" data-testid="affair-step-lost">
                  <div className="label-eyebrow">Affaire perdue</div><div className="mt-2 flex items-start gap-3"><XCircle size={28} weight="fill" className="shrink-0 text-[var(--do-accent)]" /><div><h3 className="font-heading text-lg font-black">Affaire clôturée</h3><p className="mt-1 text-sm text-[var(--do-muted)]">Cette affaire est clôturée en lecture seule.</p></div></div>
                  <div className="mt-4 rounded-xl bg-[var(--do-surface-soft)] p-3 text-sm"><strong>Motif : </strong>{selectedOpportunity.lostReason || "Non précisé"}</div>
                  <button type="button" onClick={previewSelectedQuote} disabled={!selectedOpportunity.primaryQuoteId} className="mt-4 w-full rounded-xl border border-[var(--do-border)] px-4 py-3 text-sm font-black text-[var(--do-accent)] disabled:opacity-50">Voir le devis</button>
                </section>
              )}

              {["draft", "ready"].includes(opportunityWorkflowColumn(selectedOpportunity)) && (
                <section
                  className="rounded-2xl border border-[var(--do-border)] p-4"
                  data-testid="affair-step-quote"
                >
                  <div className="label-eyebrow">
                    {opportunityQuoteStatus(selectedOpportunity) === "rejected"
                      ? "Modification demandée"
                      : opportunityWorkflowColumn(selectedOpportunity) === "ready"
                        ? "À envoyer"
                        : "Devis"}
                  </div>
                  <h3 className="mt-1 font-heading text-lg font-black">
                    {selectedOpportunity.primaryQuoteNumber || "Devis à préparer"}
                  </h3>
                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    {opportunityQuoteStatus(selectedOpportunity) === "rejected"
                      ? "L’administrateur demande une modification avant validation."
                      : opportunityWorkflowColumn(selectedOpportunity) === "ready"
                        ? "Le devis est validé et prêt à être envoyé au client."
                        : "Préparez le devis avant de poursuivre le workflow."}
                  </p>

                  {opportunityQuoteStatus(selectedOpportunity) === "rejected" && (
                    <div
                      className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3 text-sm text-[var(--do-ink)]"
                      data-testid="affair-quote-correction-reason"
                    >
                      <strong>Motif de la modification : </strong>
                      {selectedOpportunity.primaryQuoteRejectionReason
                        || "Motif non précisé"}
                    </div>
                  )}

                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {selectedOpportunity.primaryQuoteId
                      && opportunityQuoteStatus(selectedOpportunity) !== "rejected" && (
                        <button
                          type="button"
                          onClick={previewSelectedQuote}
                          className="rounded-xl border border-[var(--do-border)] px-4 py-3 text-sm font-black text-[var(--do-accent)]"
                        >
                          Voir le devis
                        </button>
                      )}
                    {opportunityWorkflowColumn(selectedOpportunity) === "ready" ? (
                      <button
                        type="button"
                        onClick={() => openSecureQuoteShare(selectedOpportunity)}
                        className="rounded-xl bg-[var(--do-accent)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)]"
                      >
                        Envoyer le devis
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setQuoteOpportunity(selectedOpportunity);
                          setSelectedOpportunity(null);
                        }}
                        className="rounded-xl bg-[var(--do-accent)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)]"
                        data-testid={
                          opportunityQuoteStatus(selectedOpportunity) === "rejected"
                            ? "affair-edit-rejected-quote"
                            : undefined
                        }
                      >
                        {selectedOpportunity.primaryQuoteId
                          ? "Modifier le devis"
                          : "Créer le devis"}
                      </button>
                    )}
                  </div>
                </section>
              )}
            </div>
          </aside>
        </div>
      )}

      {shareOpportunity?.primaryQuoteId && (
        <ShareModal
          item={{
            id: shareOpportunity.primaryQuoteId,
            type: "quote",
            name: `${shareOpportunity.primaryQuoteNumber || "Devis"} - ${shareOpportunity.title || "Affaire commerciale"}`,
            items: [
              {
                type: "quote",
                id: shareOpportunity.primaryQuoteId,
              },
            ],
          }}
          defaultProspect={
            shareOpportunity.prospectId
              ? {
                  id: shareOpportunity.prospectId,
                  firstName: shareOpportunity.prospectName || "",
                  company: shareOpportunity.prospectCompany || "",
                  email: shareOpportunity.prospectEmail || "",
                  phone: shareOpportunity.prospectPhone || "",
                }
              : null
          }
          createShare={(payload) =>
            quotesApi.share(
              shareOpportunity.primaryQuoteId,
              payload
            )
          }
          onGenerated={async () => {
            setOpenMobileColumn("client");
            toast.success(
              "Devis envoyé. L’affaire est maintenant en attente du client."
            );
            await loadBoard();
          }}
          onClose={() => {
            setShareOpportunity(null);
            loadBoard();
          }}
        />
      )}

      <OpportunityQuoteSubmitModal
        opportunity={quoteOpportunity}
        onClose={() => setQuoteOpportunity(null)}
        onSubmitted={async () => {
          setQuoteOpportunity(null);
          setSelectedOpportunity(null);
          setOpenMobileColumn("validation");
          await loadBoard();
        }}
      />

      <DirectQuoteModal
        open={directQuoteModalOpen}
        lockedProspect={directQuoteProspect}
        onClose={() => {
          setDirectQuoteModalOpen(false);
          setDirectQuoteProspect(null);
        }}
        onCreated={async ({ submitted }) => {
          setDirectQuoteProspect(null);
          setOpenMobileColumn(submitted ? "validation" : "draft");
          await loadBoard();
        }}
      />

      <OpportunityLostModal
        opportunity={lostOpportunity}
        reason={lostOpportunityReason}
        saving={
          Boolean(
            lostOpportunity &&
            movingId === lostOpportunity.id
          )
        }
        onReasonChange={
          setLostOpportunityReason
        }
        onClose={
          closeLostOpportunityModal
        }
        onSubmit={
          submitLostOpportunity
        }
      />

      <PipelineDropActionModal
        action={pipelineDropAction}
        saving={Boolean(movingId || quoteActionSaving)}
        onClose={closePipelineDropAction}
        onConfirm={confirmPipelineDropAction}
      />


    </ActaStandardPage>
  );
}
